import { useState, useEffect, useRef, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { v4 } from 'uuid'
import { PlugIntelligenceClient, type Bubble } from '../clients/PlugIntelligenceClient'
import { authenticate, getInitialMessage, MessageWithStream } from '../utils/llmChat'
import useBalances from './useBalances'
import useWallet from './useWallet'
import { logger } from '../logger'
import { wrapMessageStreamer } from '../utils/llmChat/messageStreamer'

export default () => {
  const [messages, setMessages] = useState<MessageWithStream[]>([getInitialMessage()])
  const [question, setQuestion] = useState('')
  const [bubbles, setBubbles] = useState<Bubble[]>([])
  const auth = useRef<string | null>(null)

  const { balances } = useBalances()
  const { wallet } = useWallet()
  const { t } = useTranslation()

  const sessionId = useMemo(() => v4(), [])

  const isWriting = messages.some((m) => m.stream && !m.doneStreaming)
  const disabled = isWriting || !question

  useEffect(() => {
    PlugIntelligenceClient.getBubbles().then(setBubbles).catch(logger.error)
  }, [])

  const submitQuestion = async (userQuestion: string) => {
    if (isWriting || !userQuestion) return

    const responseId = v4()

    setQuestion('')
    setMessages((prevMessages) => [
      ...prevMessages,
      { id: v4(), role: 'user', content: userQuestion },
      { id: responseId, role: 'assistant', content: '', doneStreaming: false },
    ])

    const { principalId, accountId } = wallet

    auth.current = await authenticate(auth.current, principalId)

    if (!auth.current) {
      updateMessage(responseId, {
        doneStreaming: true,
        content: t('llmChat.failedAuthMessage'),
      })

      return
    }

    const userBalances = balances
      .filter((b) => (b.usdBalance || 0) >= 0.01)
      .map((b) => ({
        token: b.token.symbol,
        balance: b.usdBalance!,
        currencySymbol: '$',
      }))

    const stream = PlugIntelligenceClient.chat({
      auth: auth.current,
      question: userQuestion,
      history: messages.map(({ role, content }) => ({ role, content })),
      sessionId,
      userBalances,
      principalId,
      accountId,
    })

    const wrappedStream = wrapMessageStreamer(stream)

    updateMessage(responseId, { stream: wrappedStream })
  }

  const updateMessage = (
    id: string,
    updates:
      | Partial<MessageWithStream>
      | ((prevMessage: MessageWithStream) => Partial<MessageWithStream>)
  ) => {
    setMessages((prevMessages) =>
      prevMessages.map((message) => {
        if (message.id !== id) return message

        const attrs = typeof updates === 'function' ? updates(message) : updates

        return { ...message, ...attrs }
      })
    )
  }

  return {
    messages,
    question,
    setQuestion,
    submitQuestion,
    bubbles,
    isWriting,
    disabled,
    updateMessage,
  }
}
